import { DateTime } from 'luxon';

export class StringUtils {
  static UPPER_CAMEL_PATTERN = /^(?:[A-Z0-9][a-z0-9]+)*$/;
  static LOWER_CAMEL_PATTERN = /^[a-z0-9]+(?:[A-Z][a-z0-9]*)*$/;
  static WORDS_PATTERN = /^[a-zA-Z0-9]+(?: [A-Za-z0-9]+)*$/;
  static KEBAB_CASE_PATTERN = /^[a-zA-Z0-9]+(?:-[A-Za-z0-9]+)*$/;
  static CODE_CONVENTION_PATTERN = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/;
  static COL_CONVENTION_PATTERN = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

  static toCodeConvention = (str) => {
    // Remove illegal characters
    str = str.replace(/[,.\\/:*?"<>|()_'\\’%\\”\\“-]/g, ' ');

    // Replace special characters with words
    str = str.replace(/&/g, 'AND');
    str = str.replace(/@/g, 'AT');
    str = str.replace(/#/g, 'NUM');

    // Replace all whitespace (including multiple spaces) with a single space
    str = str.replace(/\s+/g, ' ').trim();

    if (this.UPPER_CAMEL_PATTERN.test(str) || this.LOWER_CAMEL_PATTERN.test(str)) {
      let res = str.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase();
      return res;
    } else if (this.CODE_CONVENTION_PATTERN.test(str)) {
      return str;
    } else if (this.COL_CONVENTION_PATTERN.test(str)) {
      return str.toUpperCase();
    } else if (this.WORDS_PATTERN.test(str)) {
      let res = str.split(' ').join('_').toUpperCase();
      return res;
    } else if (this.KEBAB_CASE_PATTERN.test(str)) {
      let res = str.split('-').join('_').toUpperCase();
      return res;
    } else {
      let msg = 'Unknown pattern: ' + str;
      console.error(msg);
      throw new Error(msg);
    }
  };

  static toLowerCamel = (str) => {
    let codeConventionStr = this.toCodeConvention(str);

    const words = codeConventionStr.split('_');

    words[0] = words[0].toLowerCase();
    for (let i = 1; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substring(1).toLowerCase();
    }

    return words.join('');
  };

  static toUpperCamel = (str) => {
    let codeConventionStr = this.toCodeConvention(str);

    const words = codeConventionStr.split('_');

    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substring(1).toLowerCase();
    }

    return words.join('');
  };

  static capitalizeFirstLetter = (str) => {
    if (!str) {
      return '';
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  static toCapitalizedWords = (str) => {
    str = this.toCodeConvention(str);

    let words = str.split('_');
    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substring(1).toLowerCase();
    }

    return words.join(' ');
  };

  static toKebabConvention = (str) => {
    let codeConventionStr = this.toCodeConvention(str);
    let res = codeConventionStr.split('_').join('-').toLowerCase();
    return res;
  };

  static toColConvention = (str) => {
    let codeConventionStr = this.toCodeConvention(str);
    let res = codeConventionStr.toLowerCase();
    return res;
  };

  static isKebabConvention = (str) => {
    return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(str);
  };

  static isISODate = (str) => {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(str);
  };

  static toSingular = (str) => {
    // let esPattern = /^(?:(.*)(?<=s)(?:es))?(.*)$/;
    let esPattern = /(?:es\s)(.*)$/; // e.g. classes -> class, must have whitespace.. protecting for ex. like "tier results"
    let iesPattern = /^(?:(.*)(?:ies))$/; // ies -> y
    let sPattern = /^(?:(.*)(?<!s)s)$/; // e.g. customers -> customer
    let sesPattern = /^(?:(.*)(?<=s)(?:es))$/; //addresses to address

    if (iesPattern.test(str)) {
      str = str.replace(iesPattern, '$1y');
    } else if (sesPattern.test(str)) {
      str = str.replace(sesPattern, '$1');
    } else if (sPattern.test(str)) {
      str = str.replace(sPattern, '$1');
    } else if (esPattern.test(str)) {
      str = str.replace(esPattern, ' $1');
    }

    return str;
  };

  static thousandSeparator = (number) => {
    return number.toLocaleString('en-US');
  };

  static toLocalInstant = (instant) => {
    if (!instant) return '';
    return DateTime.fromISO(instant, { zone: 'utc' }).toLocal().toFormat('yyyy-MM-dd hh:mm:ss a');
  };

  static toPercent = (x, { digits = 0 } = {}) => {
    if (x == null || !isFinite(x)) return '—';
    const fmt = new Intl.NumberFormat(undefined, {
      style: 'percent',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    return fmt.format(x);
  };

  static formatNumber = (number, decimalPrecision, minDecimalPrecision) => {
    const valueFormat = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: minDecimalPrecision > decimalPrecision ? decimalPrecision : minDecimalPrecision,
      maximumFractionDigits: decimalPrecision,
    });

    const value = valueFormat.format(Math.abs(number));
    return number < 0 ? `(${value})` : value;
  };

  /**
   * Abbreviates a number to 3 significant digits with a K/M/B/T suffix, e.g. 23948303842 -> "23.9B".
   *
   * currency adds a dollar sign ("$23.9B"). accounting shows negatives in parentheses like formatNumber ("($8.67B)")
   * instead of with a minus sign ("-8.67B").
   */
  static formatCompactNumber = (number, { currency = false, accounting = false } = {}) => {
    if (number == null || isNaN(number)) return '—';

    const abs = Math.abs(number);

    const units = [
      { limit: 1_000_000_000_000, suffix: 'T' },
      { limit: 1_000_000_000, suffix: 'B' },
      { limit: 1_000_000, suffix: 'M' },
      { limit: 1000, suffix: 'K' },
    ];

    let divisor = 1;
    let suffix = '';
    for (const unit of units) {
      if (abs >= unit.limit) {
        divisor = unit.limit;
        suffix = unit.suffix;
        break;
      }
    }

    // Converting back to a number drops the trailing zeros toPrecision leaves behind
    const scaled = String(+(abs / divisor).toPrecision(3));

    const formatted = (currency ? '$' : '') + scaled + suffix;
    if (number >= 0) {
      return formatted;
    }
    return accounting ? `(${formatted})` : `-${formatted}`;
  };

  static colorToHexCode = (color) => {
    if (!color) return undefined;
    if (color.startsWith('#')) {
      return color;
    } else if (color.startsWith('rgb')) {
      const rgbValues = color.match(/\d+/g).map(Number);
      return `#${rgbValues.map((x) => x.toString(16).padStart(2, '0')).join('')}`;
    } else {
      const el = document.createElement('div');
      el.style.color = color;
      document.body.appendChild(el);
      const computed = getComputedStyle(el).color;
      document.body.removeChild(el);
      return computed;
    }
  };

  static toValidColor = (color) => {
    if (!color) return undefined;
    if (color.startsWith('#') || color.startsWith('rgb')) return color;
    return this.colorToHexCode(color);
  };
}
